import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MapRow {
  readonly transformationId: string;
  readonly transformationCode: string;
  readonly transformationName: string;
  readonly transformationType: string;
  readonly status: string;
}

const TYPES = ['', 'XSLT', 'JSONATA', 'MAPPING', 'SCRIPT', 'TEMPLATE'];
const STATUSES = ['', 'DRAFT', 'ACTIVE', 'RETIRED'];

/**
 * GR L29.03 — Transformation Maps (`/admin/esb/transformation-maps`). Lists the
 * `data transformation` maps that reshape a message between source and target
 * formats. Filterable by transformation type and status.
 */
export function GrEsbTransformationMapsPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<MapRow[]>([]);
  const [transformationType, setTransformationType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (transformationType) params.set('transformationType', transformationType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: MapRow[] }>(`/v1/eip/transformations${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, transformationType, status]);

  return (
    <main aria-label="esb-transformation-maps">
      <h1>{tr('gr29.maps.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr29.maps.labelType')}
          <select aria-label="filter-type" value={transformationType} onChange={(e) => setTransformationType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr29.maps.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr29.maps.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr29.maps.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr29.maps.loading')}</p> : (
        <>
          <p aria-label="map-count">{tr('gr29.maps.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr29.maps.empty')}</p> : (
            <table aria-label="map-table">
              <thead><tr><th>{tr('gr29.maps.colCode')}</th><th>{tr('gr29.maps.colName')}</th><th>{tr('gr29.maps.colType')}</th><th>{tr('gr29.maps.colStatus')}</th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.transformationId}>
                    <td>{m.transformationCode}</td><td>{m.transformationName}</td>
                    <td>{m.transformationType}</td><td>{m.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
